/**
 * SecurityAlertsFeed
 *
 * Alert feed with severity badges, acknowledge/resolve actions (admin only),
 * severity/status/category filters, and pagination (#584). The filter state is
 * held in useSecurityStore; pagination offset is local.
 */

import { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Clock, Loader2 } from 'lucide-react';
import { useSecurityAlerts, useResolveAlert } from '../../hooks/useSecurity';
import { useSecurityStore } from '../../store/securityStore';
import { useAuthStore } from '../../store/authStore';
import { AlertAcknowledgeModal } from './AlertAcknowledgeModal';
import type { SecurityAlert } from '../../types/security.types';

const ADMIN_ROLES = ['platform_admin', 'parent_org_admin', 'customer_admin'];

const PAGE_SIZE = 25;

// Categories emitted by the alert engine (security-alert-engine.service.ts) plus
// the auth-lockout path. Empty value => no category filter (all categories).
const CATEGORY_OPTIONS = [
  { value: '', label: 'All categories' },
  { value: 'auth_anomaly', label: 'Auth anomaly' },
  { value: 'routing_anomaly', label: 'Routing anomaly' },
  { value: 'tunnel_failure', label: 'Tunnel failure' },
  { value: 'tunnel_degraded', label: 'Tunnel degraded' },
  { value: 'vpn_ddns_drift', label: 'VPN DDNS drift' },
];

const SELECT_CLASS =
  'text-xs border border-ems-border rounded-lg px-2 py-1 bg-ems-bg text-ems-text-primary focus:outline-none focus:ring-2 focus:ring-ems-green/20';

const SEVERITY_STYLES: Record<string, string> = {
  critical: 'bg-ems-red/10 text-ems-red border-ems-red/20',
  high: 'bg-ems-amber/10 text-ems-amber border-ems-amber/20',
  medium: 'bg-ems-amber/10 text-ems-amber border-ems-amber/20',
  low: 'bg-ems-sky/10 text-ems-sky border-ems-sky/20',
};

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-ems-red/10 text-ems-red',
  acknowledged: 'bg-ems-amber/10 text-ems-amber',
  resolved: 'bg-ems-green/10 text-ems-green',
  dismissed: 'bg-ems-bg-card-hover text-ems-text-muted',
};

export function SecurityAlertsFeed() {
  const {
    alertSeverityFilter,
    alertStatusFilter,
    alertCategoryFilter,
    setAlertSeverityFilter,
    setAlertStatusFilter,
    setAlertCategoryFilter,
  } = useSecurityStore();
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role && ADMIN_ROLES.includes(role);

  const [page, setPage] = useState(0);

  const { data, isLoading } = useSecurityAlerts({
    severity: alertSeverityFilter,
    status: alertStatusFilter || 'active',
    category: alertCategoryFilter,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const resolveMutation = useResolveAlert();
  const [ackAlert, setAckAlert] = useState<SecurityAlert | null>(null);

  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Clamp the page if the backlog shrank beneath the current offset (e.g. after
  // a resolve/acknowledge refetch) so we never strand the user on an empty page.
  useEffect(() => {
    if (totalPages > 0 && page > totalPages - 1) {
      setPage(totalPages - 1);
    }
  }, [totalPages, page]);

  const handleResolve = async (alertId: string) => {
    await resolveMutation.mutateAsync(alertId);
  };

  return (
    <div className="bg-ems-bg-card rounded-2xl border border-ems-border overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-b border-ems-border">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-ems-text-muted" />
          <h2 className="text-sm font-semibold text-ems-text-primary uppercase tracking-wider">Security Alerts</h2>
          <span className="text-xs text-ems-text-muted">({total} total)</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Filter by severity"
            value={alertSeverityFilter || ''}
            onChange={(e) => { setAlertSeverityFilter(e.target.value || undefined); setPage(0); }}
            className={SELECT_CLASS}
          >
            <option value="">All severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            aria-label="Filter by status"
            value={alertStatusFilter || 'active'}
            onChange={(e) => { setAlertStatusFilter(e.target.value); setPage(0); }}
            className={SELECT_CLASS}
          >
            <option value="active">Active</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>
          <select
            aria-label="Filter by category"
            value={alertCategoryFilter || ''}
            onChange={(e) => { setAlertCategoryFilter(e.target.value || undefined); setPage(0); }}
            className={SELECT_CLASS}
          >
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-ems-text-muted" />
        </div>
      ) : !data?.alerts?.length ? (
        <div className="px-5 py-8 text-center">
          <CheckCircle className="w-8 h-8 text-ems-green/30 mx-auto mb-2" />
          <p className="text-sm text-ems-text-muted">No security alerts match the current filters</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ems-border bg-ems-bg-card-hover">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-ems-text-muted uppercase">Severity</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-ems-text-muted uppercase">Alert</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-ems-text-muted uppercase">Site</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-ems-text-muted uppercase">Category</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-ems-text-muted uppercase">Status</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-ems-text-muted uppercase">Time</th>
                {isAdmin && (
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-ems-text-muted uppercase">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {data.alerts.map((alert) => (
                <tr key={alert.alert_id} className="border-b border-ems-border hover:bg-ems-bg-card-hover">
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${SEVERITY_STYLES[alert.severity] || ''}`}>
                      <AlertTriangle className="w-3 h-3" />
                      {alert.severity}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <p className="text-ems-text-primary text-xs font-medium">{alert.title}</p>
                    {alert.description && (
                      <p className="text-ems-text-muted text-[11px] mt-0.5 line-clamp-1">{alert.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-ems-text-secondary text-xs">{alert.site_name || '—'}</td>
                  <td className="px-4 py-2.5 text-ems-text-secondary text-xs font-mono">{alert.category}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[alert.status] || ''}`}>
                      {alert.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-ems-text-muted text-xs">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(alert.created_at).toLocaleString()}
                    </div>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {alert.status === 'active' && (
                          <button
                            onClick={() => setAckAlert(alert)}
                            className="px-2 py-1 text-[11px] font-medium text-ems-green border border-ems-green/20 rounded hover:bg-ems-green/5 transition-colors"
                          >
                            Acknowledge
                          </button>
                        )}
                        {(alert.status === 'active' || alert.status === 'acknowledged') && (
                          <button
                            onClick={() => handleResolve(alert.alert_id)}
                            disabled={resolveMutation.isPending}
                            className="px-2 py-1 text-[11px] font-medium text-ems-green border border-ems-green/20 rounded hover:bg-ems-green/5 transition-colors disabled:opacity-50"
                          >
                            Resolve
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-ems-border">
          <span className="text-xs text-ems-text-muted">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-2 py-1 text-xs rounded border border-ems-border disabled:opacity-50 hover:bg-ems-bg-card-hover text-ems-text-secondary"
            >
              Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-2 py-1 text-xs rounded border border-ems-border disabled:opacity-50 hover:bg-ems-bg-card-hover text-ems-text-secondary"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Acknowledge Modal */}
      {ackAlert && (
        <AlertAcknowledgeModal
          alert={ackAlert}
          onClose={() => setAckAlert(null)}
        />
      )}
    </div>
  );
}
